import axios from 'axios';

// API Base URL - Create React App handles process.env at build time
declare const process: any;

const API_BASE_URL = process.env?.REACT_APP_API_URL || 
  (process.env?.NODE_ENV === 'production' ? 'https://plantedhire.com/api' : 'http://localhost:5001/api');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('API Request interceptor:', {
      url: config.url,
      hasToken: !!token,
      userAgent: navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') ? 'Safari' : 'Other'
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('customer');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  requestVerificationCode: (email: string) =>
    api.post('/auth/request-verification-code', { email }),
  
  verifyCode: (email: string, code: string) =>
    api.post('/auth/verify-code', { email, code }),
  
  register: (customerData: {
    customerName: string;
    customerAddress: string;
    userName: string;
    email: string;
    password: string;
    verificationCode: string;
  }) =>
    api.post('/auth/register', customerData),
  
  getProfile: () =>
    api.get('/auth/profile'),
  
  verifyToken: () =>
    api.get('/auth/verify'),
  
  updateProfile: (data: { name?: string; email?: string }) =>
    api.put('/auth/profile', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', data),
  
  uploadProfilePicture: (file: File) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    return api.post('/auth/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Customer API
export const customerAPI = {
  get: () =>
    api.get('/customers'),
  
  update: (data: { name?: string; address?: string }) =>
    api.put('/customers', data),
  
  getStats: () =>
    api.get('/customers/stats'),
};

// Users API
export const usersAPI = {
  getAll: () =>
    api.get('/users'),
  
  create: (data: {
    name: string;
    email: string;
    password: string;
    role?: string;
  }) =>
    api.post('/users', data),
  
  update: (id: string, data: {
    name?: string;
    email?: string;
    role?: string;
    is_active?: boolean;
  }) =>
    api.put(`/users/${id}`, data),
  
  updateLanguage: (language: string) =>
    api.put('/users/language', { language }),
  
  getReminderSettings: () =>
    api.get('/users/reminder-settings'),
  
  updateReminderSettings: (data: {
    email_reminders_enabled: boolean;
    reminder_time: string;
    reminder_timezone: string;
  }) =>
    api.put('/users/reminder-settings', data),
  
  getLocationSettings: () =>
    api.get('/users/location-settings'),
  
  updateLocationSettings: (data: {
    postcode?: string;
    latitude?: number;
    longitude?: number;
  }) =>
    api.put('/users/location-settings', data),
  
  delete: (id: string) =>
    api.delete(`/users/${id}`),
};

// Invitations API
export const invitationsAPI = {
  create: (data: { email: string; name?: string; role?: 'admin' | 'user' | 'viewer' }) =>
    api.post('/invitations', data),
  validate: (token: string) =>
    api.get(`/invitations/validate`, { params: { token } }),
  accept: (data: { token: string; name: string; password: string }) =>
    api.post('/invitations/accept', data),
};

// Equipment API
export const equipmentAPI = {
  getAll: (params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get('/equipment', { params }),
  
  getById: (id: string) =>
    api.get(`/equipment/${id}`),
  
  create: (data: {
    name: string;
    description?: string;
    category: string;
    unit_of_measure?: string;
    power?: string;
    image?: File;
  }) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    formData.append('category', data.category);
    if (data.unit_of_measure) formData.append('unit_of_measure', data.unit_of_measure);
    if (data.power) formData.append('power', data.power);
    if (data.image) formData.append('image', data.image);
    
    return api.post('/equipment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  update: (id: string, data: {
    name?: string;
    description?: string;
    category?: string;
    unit_of_measure?: string;
    power?: string;
    is_active?: boolean;
    image?: File;
  }) => {
    const formData = new FormData();
    if (data.name !== undefined) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.category !== undefined) formData.append('category', data.category);
    if (data.unit_of_measure !== undefined) formData.append('unit_of_measure', data.unit_of_measure);
    if (data.power !== undefined) formData.append('power', data.power);
    if (data.is_active !== undefined) formData.append('is_active', data.is_active.toString());
    if (data.image) formData.append('image', data.image);
    
    return api.put(`/equipment/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  delete: (id: string) =>
    api.delete(`/equipment/${id}`),
  
  getCategories: () =>
    api.get('/equipment/categories'),
  
  getFavorites: (params?: {
    page?: number;
    limit?: number;
  }) =>
    api.get('/equipment/favorites/list', { params }),
  
  addFavorite: (id: string) =>
    api.post(`/equipment/${id}/favorite`),
  
  removeFavorite: (id: string) =>
    api.delete(`/equipment/${id}/favorite`),
  
  getImageOptions: (id: string) =>
    api.get(`/equipment/${id}/image-options`),
  
  setStockImage: (id: string, data: {
    imageId: string;
    imageUrl: string;
  }) =>
    api.post(`/equipment/${id}/set-stock-image`, data),
  
  setAutoImage: (id: string) =>
    api.post(`/equipment/${id}/auto-image`),

  getPricing: () =>
    api.get('/equipment/pricing'),

  generateDescription: (data: {
    name: string;
    category: string;
    unit_of_measure: string;
    power: string;
  }) =>
    api.post('/equipment/generate-description', data),

  generateCustomImage: (id: string, data: {
    prompt: string;
    equipment_name: string;
    category: string;
    power: string;
  }) =>
    api.post(`/equipment/${id}/generate-custom-image`, data),
};

// Suppliers API
export const suppliersAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) =>
    api.get('/suppliers', { params }),
  
  getById: (id: string) =>
    api.get(`/suppliers/${id}`),
  
  create: (data: {
    name: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
  }) =>
    api.post('/suppliers', data),
  
  update: (id: string, data: {
    name?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    is_active?: boolean;
  }) =>
    api.put(`/suppliers/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/suppliers/${id}`),
  
  updateCustomerDetails: (id: string, data: {
    contact_name?: string;
    contact_email?: string;
    phone?: string;
    preferred_order_method?: 'phone' | 'email' | 'purchase_order' | 'zahara';
    availability_check_method?: 'phone' | 'email' | 'website';
  }) =>
    api.post(`/suppliers/${id}/customer-details`, data),
  
  removeCustomerDetails: (id: string) =>
    api.delete(`/suppliers/${id}/customer-details`),
  
  getFavorites: (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) =>
    api.get('/suppliers/favorites/list', { params }),
  
  getMapData: (nearMe: boolean = false) =>
    api.get(`/suppliers/map-data?nearMe=${nearMe}`),
  
  addFavorite: (id: string) =>
    api.post(`/suppliers/${id}/favorite`),
  
  removeFavorite: (id: string) =>
    api.delete(`/suppliers/${id}/favorite`),
};

// Sites API
export const sitesAPI = {
  getAll: () =>
    api.get('/sites'),
  
  getById: (id: string) =>
    api.get(`/sites/${id}`),
  
  create: (data: {
    name: string;
    address_line_1: string;
    address_line_2?: string;
    city: string;
    county?: string;
    postcode: string;
    country?: string;
    job_number?: string;
    status?: 'active' | 'inactive' | 'completed';
  }) =>
    api.post('/sites', data),
  
  update: (id: string, data: {
    name?: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    county?: string;
    postcode?: string;
    country?: string;
    job_number?: string;
    status?: 'active' | 'inactive' | 'completed';
  }) =>
    api.put(`/sites/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/sites/${id}`),
};

// Hires API
export const hiresAPI = {
  getAll: (page = 1, limit = 12, search = '', status?: string) => {
    let url = `/hires?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    return api.get(url);
  },
  
  getById: (id: string) =>
    api.get(`/hires/${id}`),
  
  create: (data: {
    equipment_id: string;
    supplier_id: string;
    site_id: string;
    price: number;
    pricing_term: 'fixed' | 'day' | 'week' | 'month' | 'year';
    hire_start_date: string;
    hire_end_date: string;
    hire_duration_days?: number;
    total_cost?: number;
    hire_status?: 'active' | 'completed' | 'collection_booked';
  }) =>
    api.post('/hires', data),
  
  update: (id: string, data: {
    equipment_id?: string;
    supplier_id?: string;
    site_id?: string;
    price?: number;
    pricing_term?: 'fixed' | 'day' | 'week' | 'month' | 'year';
    hire_start_date?: string;
    hire_end_date?: string;
    hire_duration_days?: number;
    total_cost?: number;
    hire_status?: 'active' | 'completed' | 'collection_booked';
  }) =>
    api.put(`/hires/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/hires/${id}`),
  
  bulkUpdateStatus: (hireIds: string[], status: 'active' | 'completed' | 'collection_booked') =>
    api.put('/hires/bulk/status', { hireIds, status }),
  
  bulkDelete: (hireIds: string[]) =>
    api.delete('/hires/bulk', { data: { hireIds } }),
  
  bulkExtend: (hireIds: string[], newEndDate: string, zaharaAction: 'none' | 'new_order' | 'update_existing' = 'none') =>
    api.put('/hires/bulk/extend', { hireIds, newEndDate, zaharaAction }),
  
  getStats: () =>
    api.get('/hires/stats/summary'),
  
  getOverdue: () =>
    api.get('/hires/overdue'),
  
  getSubscriptionUsage: () =>
    api.get('/hires/subscription-usage'),
  
  getEndingSoon: () =>
    api.get('/hires/ending-soon'),
  
  sendBookingEmail: (hireIds: string[], supplierId: string) =>
    api.post('/hires/send-booking-email', { hireIds, supplierId }),
};

// Zahara API
export const zaharaAPI = {
  getSettings: () => api.get('/zahara/settings'),
  saveSettings: (settings: any) => api.post('/zahara/settings', settings),
  lookupNominalCode: (code: string) => api.post('/zahara/lookup-nominal-code', { code }),
  lookupTaxCode: (code: string) => api.post('/zahara/lookup-tax-code', { code }),
  lookupCostCode: (code: string) => api.post('/zahara/lookup-cost-code', { code }),
  lookupDivision: (name: string) => api.post('/zahara/lookup-division', { name }),
  getSupplierMatches: () => api.get('/zahara/supplier-matches'),
  matchSupplier: (supplierId: string, zaharaSupplierId: string) => 
    api.post('/zahara/match-supplier', { supplierId, zaharaSupplierId }),
  exportSupplier: (supplierId: string) => api.post('/zahara/export-supplier', { supplierId }),
  getProjects: () => api.get('/zahara/projects'),
  syncProjects: () => api.post('/zahara/projects/sync'),
  getSyncStatus: () => api.get('/zahara/sync-status'),
  createPurchaseOrder: (hireId: string) => api.post('/zahara/purchase-order/create', { hireId }),
  createBulkPurchaseOrder: (hireIds: string[]) => api.post('/zahara/purchase-order/create-bulk', { hireIds })
};

// Subscription API
export const subscriptionAPI = {
  getPlans: () =>
    api.get('/subscriptions/plans'),
  
  createCheckout: (priceId: string, billingInterval: string) =>
    api.post('/subscriptions/checkout', { priceId, billingInterval }),
  
  handleSuccess: (sessionId: string) =>
    api.post('/subscriptions/success', { sessionId }),
  
  getStatus: () =>
    api.get('/subscriptions/status'),
  
  cancel: () =>
    api.post('/subscriptions/cancel'),
  
  downgradeToFree: () =>
    api.post('/subscriptions/downgrade-to-free'),
};

export default api;
